import React from "react";
import { useTranslation } from "react-i18next";
import ReactSwitch from "react-switch";
import { PlusIcon, TrashIcon, StyledIconButton } from "@aureskonnect/react-ui";

import BannerImages from "../../../Common/BannerImages";
import LanguageTable from "../../../Common/LanguageTable";
import { InfoBlockConfig, SWITCH_PROPS_SMALL } from "../constants";
import { ColorPickerField } from "./ColorPickerField";

interface Props {
    basePath: string[];
    infoBlock: any;
    defaultInfoBlock: InfoBlockConfig;
    mainColor: string;
    updatePath: (path: string[], value: any) => void;
}

export function InfoBlockSection({
    basePath,
    infoBlock,
    defaultInfoBlock,
    mainColor,
    updatePath,
}: Props) {
    const { t } = useTranslation();
    const bgColor = infoBlock?.BackgroundColor || defaultInfoBlock.BackgroundColor;

    return (
        <div
            data-testid="info-block-section"
            style={{
                marginBottom: 24,
                padding: 16,
                border: "1px solid #e0e0e0",
                borderRadius: 8,
            }}
        >
            <h6
                style={{
                    fontWeight: 700,
                    fontSize: 15,
                    marginBottom: 16,
                    color: mainColor,
                    textTransform: "uppercase",
                }}
            >
                {t("Info block (sidebar)")}
            </h6>

            {/* Background color */}
            <div className="mb-3">
                <ColorPickerField
                    label={t("Background color")}
                    color={bgColor}
                    onChange={(hex) =>
                        updatePath(
                            [...basePath, "InfoBlock", "BackgroundColor"],
                            hex
                        )
                    }
                />
            </div>

            {/* Background image */}
            <div className="mb-3">
                <BannerImages
                    dataPath={[...basePath, "InfoBlock", "Image", "Desktop"]}
                    title={t("Background image")}
                    size="400*800 (png, jpg, jpeg)"
                />
            </div>

            {/* Title & Description */}
            <LanguageTable
                path={[...basePath, "InfoBlock"]}
                fields={[
                    {
                        key: "Title",
                        label: "Title",
                        placeholder: "Enter title",
                        isVisible: infoBlock?.TitleVisible ?? true,
                        onVisibilityToggle: () =>
                            updatePath(
                                [...basePath, "InfoBlock", "TitleVisible"],
                                !(infoBlock?.TitleVisible ?? true)
                            ),
                    },
                    {
                        key: "Description",
                        label: "Description",
                        placeholder: "Enter description",
                        isVisible: infoBlock?.DescriptionVisible ?? true,
                        onVisibilityToggle: () =>
                            updatePath(
                                [...basePath, "InfoBlock", "DescriptionVisible"],
                                !(infoBlock?.DescriptionVisible ?? true)
                            ),
                    },
                ]}
                defaultTexts={defaultInfoBlock}
                sectionTitle={t("Title and description")}
            />

            {/* Advantages list */}
            <AdvantagesList
                basePath={basePath}
                infoBlock={infoBlock}
                defaultInfoBlock={defaultInfoBlock}
                updatePath={updatePath}
            />
        </div>
    );
}

/* ── Advantages sub-component ── */

function AdvantagesList({
    basePath,
    infoBlock,
    defaultInfoBlock,
    updatePath,
}: {
    basePath: string[];
    infoBlock: any;
    defaultInfoBlock: InfoBlockConfig;
    updatePath: (path: string[], value: any) => void;
}) {
    const { t } = useTranslation();
    const advantages = infoBlock?.Advantages || defaultInfoBlock.Advantages;

    return (
        <div data-testid="advantages-list" style={{ marginTop: 12 }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>
                    {t("Advantages list")}
                </h6>
                <StyledIconButton
                    data-testid="add-advantage-btn"
                    title={t("Add")}
                    outline={true}
                    variant="success"
                    className="m-0"
                    style={{ padding: "4px 8px" }}
                    onClick={() => {
                        const updated = [...(infoBlock?.Advantages || [])];
                        updated.push({
                            Text: { FR: "", EN: "", ES: "", IT: "", AL: "" },
                            Activer: true,
                        });
                        updatePath(
                            [...basePath, "InfoBlock", "Advantages"],
                            updated
                        );
                    }}
                >
                    <PlusIcon height={16} width={16} />
                </StyledIconButton>
            </div>

            {advantages.map((adv: any, idx: number) => (
                <div
                    key={idx}
                    data-testid={`advantage-item-${idx}`}
                    className="d-flex align-items-center mb-2"
                    style={{
                        padding: "8px 12px",
                        background: "#f8f9fa",
                        borderRadius: 6,
                        gap: 8,
                    }}
                >
                    <span
                        style={{
                            color: "#34C38F",
                            fontWeight: 700,
                            fontSize: 16,
                            flexShrink: 0,
                        }}
                    >
                        ✓
                    </span>
                    <input
                        data-testid={`advantage-input-${idx}`}
                        type="text"
                        value={adv?.Text?.FR || ""}
                        onChange={(e) =>
                            updatePath(
                                [
                                    ...basePath,
                                    "InfoBlock",
                                    "Advantages",
                                    String(idx),
                                    "Text",
                                    "FR",
                                ],
                                e.target.value
                            )
                        }
                        placeholder={t("Enter advantage text")}
                        style={{
                            flex: 1,
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            padding: "4px 8px",
                            fontSize: 13,
                        }}
                    />
                    <ReactSwitch
                        data-testid={`advantage-switch-${idx}`}
                        {...SWITCH_PROPS_SMALL}
                        checked={adv?.Activer ?? true}
                        onChange={() =>
                            updatePath(
                                [
                                    ...basePath,
                                    "InfoBlock",
                                    "Advantages",
                                    String(idx),
                                    "Activer",
                                ],
                                !(adv?.Activer ?? true)
                            )
                        }
                    />
                    <StyledIconButton
                        data-testid={`delete-advantage-btn-${idx}`}
                        title={t("Delete")}
                        outline={true}
                        variant="danger"
                        className="m-0"
                        style={{ padding: "2px 6px" }}
                        onClick={() => {
                            const updated = [
                                ...(infoBlock?.Advantages || []),
                            ].filter((_: any, i: number) => i !== idx);
                            updatePath(
                                [...basePath, "InfoBlock", "Advantages"],
                                updated
                            );
                        }}
                    >
                        <TrashIcon
                            height={14}
                            width={14}
                            style={{ fill: "red" }}
                        />
                    </StyledIconButton>
                </div>
            ))}
        </div>
    );
}
